import { useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { ClockCounterClockwise, FileXls, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Latest + every previous revision of each generated document. */
export default function DocRevisions({ pid, refreshKey }) {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    if (!pid) return;
    api.get(`/documents/${pid}/revisions`)
      .then((r) => setGroups(r.data.groups || []))
      .catch(() => setGroups([]));
  }, [pid, refreshKey]);

  const view = async (rid) => {
    try {
      const { data } = await api.get(`/documents/${pid}/revisions/${rid}`);
      const w = window.open("", "_blank");
      w.document.write(`<pre style="font:12px/1.5 monospace;padding:16px">${
        JSON.stringify(data.payload, null, 2).replace(/</g, "&lt;")}</pre>`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!groups.length) return null;

  return (
    <div className="mt-4 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm"
      data-testid="doc-revisions">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--c-border)]">
        <ClockCounterClockwise size={14} className="text-[var(--c-accent)]" />
        <span className="text-xs font-medium text-[var(--c-text)]">Document revisions</span>
        <span className="text-[10px] font-mono text-[var(--c-muted)] ml-auto">
          {groups.reduce((a, g) => a + g.revisions.length, 0)} stored
        </span>
      </div>
      {groups.map((g) => (
        <div key={g.kind} className="border-b border-[var(--c-border)]/50 last:border-0">
          <button data-testid={`doc-rev-group-${g.kind}`}
            onClick={() => setOpen((o) => ({ ...o, [g.kind]: !o[g.kind] }))}
            className="w-full flex items-center gap-2 px-4 py-2 text-[11px] text-[var(--c-text)] hover:bg-[var(--c-surface-2)]/70">
            {open[g.kind] ? <CaretDown size={11} /> : <CaretRight size={11} />}
            {g.title}
            <span className="ml-auto font-mono text-[10px] text-[var(--c-muted)]">{g.revisions.length} rev</span>
          </button>
          {open[g.kind] && (
            <div className="pb-2">
              {g.revisions.map((r) => (
                <div key={r.id} data-testid={`doc-rev-${r.id}`}
                  className="flex items-center gap-2 pl-9 pr-4 py-1.5 text-[11px] text-[var(--c-muted)]">
                  <span className="font-mono text-[var(--c-text)]">Rev {r.rev}</span>
                  {r.latest && (
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[var(--c-green)]/15 text-[var(--c-green)]">
                      latest
                    </span>
                  )}
                  <span className="font-mono text-[10px]">{(r.generated_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="truncate">{r.generated_by}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button data-testid={`doc-rev-view-${r.id}`} onClick={() => view(r.id)}
                      className="text-[var(--c-accent)] hover:underline">View</button>
                    {g.kind === "design_sheet" && (
                      <a data-testid={`doc-rev-xlsx-${r.id}`}
                        href={`${BASE}/api/documents/${pid}/revisions/${r.id}/export.xlsx`}
                        className="inline-flex items-center gap-1 text-[var(--c-accent)] hover:underline">
                        <FileXls size={12} /> xlsx
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
